import { shouldDisplayConsoleDiagnostics } from './devDiagnostics';

// Error Logger - Captures all console errors and logs for debugging
class ErrorLogger {
  constructor() {
    this.errors = [];
    this.logs = [];
    this.showConsoleDiagnostics = shouldDisplayConsoleDiagnostics();
    this.setupInterceptors();
  }

  /**
   * Detects if an error is a chunk/module load failure (common after deployments)
   * These happen when the browser has a cached index.html referencing old chunk hashes
   */
  isChunkLoadError(error) {
    if (!error) return false;
    const message = String(error.message || error.reason || error || '');
    return (
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Loading chunk') ||
      message.includes('Loading CSS chunk') ||
      message.includes('ChunkLoadError') ||
      message.includes('error loading dynamically imported module') ||
      // MIME type error when Firebase serves index.html instead of missing chunk
      (message.includes('MIME type') && message.includes('text/html'))
    );
  }

  /**
   * Handle chunk load errors by clearing caches and reloading
   * This ensures users get the latest version after deployment
   */
  handleChunkLoadError() {
    // Prevent infinite reload loops - check if we already tried reloading
    const reloadKey = 'chunkErrorReload';
    const lastReload = sessionStorage.getItem(reloadKey);
    const now = Date.now();

    // If we reloaded in the last 30 seconds, don't reload again
    if (lastReload && (now - parseInt(lastReload, 10)) < 30000) {
      console.warn('Chunk load error persists after reload. Please clear browser cache.');
      this.displayError('Update Required', 'Please refresh the page (Cmd+Shift+R) or clear your browser cache.');
      return;
    }

    // Mark that we're reloading
    sessionStorage.setItem(reloadKey, String(now));

    console.log('Detected chunk load error - new version available. Reloading...');

    // Try to unregister service worker and clear caches
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.unregister();
        });
      });
    }

    // Clear caches if available
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    // Force reload from server, bypassing cache
    window.location.reload();
  }

  setupInterceptors() {
    // Capture console.error
    const originalError = console.error;
    console.error = (...args) => {
      this.errors.push({
        timestamp: new Date().toISOString(),
        type: 'console.error',
        message: args.join(' '),
        stack: new Error().stack
      });
      originalError.apply(console, args);
      this.displayError('Console Error', args.join(' '));
    };

    // Capture unhandled errors
    window.addEventListener('error', (event) => {
      // Check for chunk load errors
      if (this.isChunkLoadError(event)) {
        this.handleChunkLoadError();
        return;
      }

      this.errors.push({
        timestamp: new Date().toISOString(),
        type: 'window.error',
        message: event.message,
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
        stack: event.error?.stack
      });
      this.displayError('Window Error', event.message);
    });

    // Capture promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      // Check for chunk load errors (common with dynamic imports)
      if (this.isChunkLoadError(event.reason)) {
        this.handleChunkLoadError();
        return;
      }

      this.errors.push({
        timestamp: new Date().toISOString(),
        type: 'promise.rejection',
        reason: event.reason,
        promise: event.promise
      });
      this.displayError('Promise Rejection', event.reason);
    });

    // Capture console.log for debugging
    const originalLog = console.log;
    console.log = (...args) => {
      this.logs.push({
        timestamp: new Date().toISOString(),
        message: args.join(' ')
      });
      originalLog.apply(console, args);
    };
  }

  displayError(type, message) {
    if (!this.showConsoleDiagnostics || typeof document === 'undefined' || !document.body) {
      return;
    }

    // Show error in a non-intrusive way
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #ff4444;
      color: white;
      padding: 10px 15px;
      border-radius: 5px;
      z-index: 10000;
      max-width: 400px;
      font-family: monospace;
      font-size: 12px;
    `;

    const title = document.createElement('strong');
    title.textContent = `${type}:`;
    const body = document.createElement('span');
    body.textContent = String(message).substring(0, 200);
    const hint = document.createElement('small');
    hint.textContent = 'Press Cmd+Shift+L to copy all logs';

    errorDiv.append(title, document.createElement('br'), body, document.createElement('br'), hint);
    document.body.appendChild(errorDiv);
    setTimeout(() => errorDiv.remove(), 5000);
  }

  getAllLogs() {
    return {
      errors: this.errors,
      logs: this.logs,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };
  }

  copyLogsToClipboard() {
    const logs = JSON.stringify(this.getAllLogs(), null, 2);
    navigator.clipboard.writeText(logs).then(() => {
      console.log('%c📋 Logs copied to clipboard! Paste them to Claude.', 'color: green; font-weight: bold');
    });
  }
}

// Initialize logger
const errorLogger = new ErrorLogger();

// Add keyboard shortcut to copy logs
document.addEventListener('keydown', (e) => {
  if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'L') {
    e.preventDefault();
    errorLogger.copyLogsToClipboard();
  }
});

// Expose to window for manual access
window.errorLogger = errorLogger;

export default errorLogger;
